import { defineAsyncComponent } from "vue";
import lodash from "lodash";
import {
    createFieldInput,
    createFieldInputNumber,
    createFieldSelect,
    createFieldTemplateBuilder
} from "../../../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { conditionFormChecker, putField } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";

const CSelect = defineAsyncComponent(() => import("../../../../../components/input/template-builder/select/CSelect"));
const InputNumber = defineAsyncComponent(() => import("../../../../../components/input/template-builder/input-number/InputNumber"));
const HazDiscussPictogramList = defineAsyncComponent(() => import("./HazDiscussPictogramList"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/hazDiscussMainSubStance";

export const callOperator = ({ searchQuery }) => ["=", "!=", ">", "<", ">=", "<="].filter(d => d.includes(searchQuery || ""));
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callLd50 = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/ld50/cacheFindAll", { where: [{ name: "nameTh", operator: "like", value: searchQuery }] });
export const callHazSubStanceUnit = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "HAZ_SUB_STANCE_UNIT", value: searchQuery });
export const callSubStance = ({ searchQuery, refTypeKeyIn }) => {
    refTypeKeyIn.push("media");
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refType.key", type: "String", operator: "in", value: refTypeKeyIn },
        { logic: "OR", name: "refType", type: "UUID", operator: "=", value: null }
    ];
    if (searchQuery) {
        where.push({ name: "name", type: "String", operator: "like", value: searchQuery });
        where.push({ logic: "OR", name: "nameEn", type: "String", operator: "like", value: searchQuery });
        where.push({ logic: "OR", name: "refType.value", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/master/hazSubStance/search", {
            criteria: {
                select: ["**"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "name", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callUserCompany = () => [];
export const callHazDiscussCase = ({ criteria }) => store.dispatch(`resource/v1/master/hazDiscussCase/cacheFindAll`, { criteria });

export const createField = ({ props, data, key /* , isLoading */ }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}refSubStance`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazDiscussMainSubStance.refSubStance"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refSubStance,
                                        "onUpdate:value": createOnUpdateRefSubStance({ props, data, key }),
                                        disabled:
                                            viewOnly
                                            || !modelRef.refHazDiscussTemp.refMainOperation
                                            || !modelRef.refHazDiscussTemp.refSubOperation
                                            || !modelRef.refHazDiscussTemp.refMainObjective
                                            || (modelRef.refHazDiscussTemp.refMainObjective?.key === "3" && !modelRef.refHazDiscussTemp.refSubObjective),
                                        list: ({ searchQuery }) =>
                                            callSubStance({
                                                searchQuery,
                                                refTypeKeyIn: [
                                                    ["chemi", "chemiTechnicalGrade"].includes(modelRef.refHazDiscussTemp.refSubStanceTypeMain?.key)
                                                        ? "chemi"
                                                        : "micro"
                                                ]
                                            }).then(subStances => filterSubStance({ subStances, hazDiscuss: modelRef.refHazDiscussTemp })),
                                        renderLabel: ({ item }) => <div>{item.nameEn || item.name} {item.refType?.value ? "(" + item.refType?.value + ")": ""}</div>
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameEn`) }
                        }),
                        {
                            visible: () => !modelRef.refHazDiscussTemp.refMainOperation
                                || !modelRef.refHazDiscussTemp.refSubOperation
                                || !modelRef.refHazDiscussTemp.refMainObjective
                                || (modelRef.refHazDiscussTemp.refMainObjective?.key === "3" && !modelRef.refHazDiscussTemp.refSubObjective),
                            colProp: () => ({ xs: 24 }),
                            formItemRender: () => <span className="text-danger pl-5">{i18n.global.t("views.request.hazDiscuss.error.requireMainSubOperationObjective")}</span>
                        },
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}otherSubStance`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazDiscussMainSubStance.otherSubStance"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.otherSubStance,
                                        "onUpdate:value": value => (model.otherSubStance = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                        createFieldInputNumber({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}intense`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazDiscussMainSubStance.intense"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.intense,
                                        "onUpdate:value": value => (model.intense = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        addonAfter: () => <div>%</div>
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        })
                    ],
                    skeleton: isLoading.value
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        {
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}microAnalysisQty1`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("views.request.hazDiscussProduct.microAnalysisQty"),
                            inputRender: () => {
                                const inputPropField1 = lodash.merge(
                                    {
                                        value: model.microAnalysisQty1,
                                        "onUpdate:value": value => (model.microAnalysisQty1 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisQty1` }, model }
                                    })
                                );
                                const inputPropField2 = lodash.merge(
                                    {
                                        value: model.microAnalysisQty2,
                                        "onUpdate:value": value => (model.microAnalysisQty2 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisQty2` }, model }
                                    })
                                );
                                const inputPropField3 = lodash.merge(
                                    {
                                        value: model.microAnalysisQty3,
                                        "onUpdate:value": value => (model.microAnalysisQty3 = value),
                                        disabled: viewOnly,
                                        allowPrecision: true,
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}microAnalysisQty3` }, model }
                                    })
                                );
                                const inputPropField4 = lodash.merge(
                                    {
                                        value: model.refMicroUnit,
                                        "onUpdate:value": value => (model.refMicroUnit = value),
                                        disabled: viewOnly,
                                        list: callHazSubStanceUnit,
                                        optLabel: "value",
                                        placeholder: i18n.global.t("button.unit"),
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}refMicroUnit` }, model }
                                    })
                                );
                                return (
                                    <>
                                        <InputNumber {...inputPropField1}></InputNumber>
                                        <span> x </span>
                                        <InputNumber {...inputPropField2}></InputNumber>
                                        <span> ^ </span>
                                        <InputNumber {...inputPropField3}></InputNumber>
                                        <span> </span>
                                        <CSelect {...inputPropField4}></CSelect>
                                    </>
                                );
                            }
                        },
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                            key: `${prefix}refSubStance.casNo`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazDiscussMainSubStance.casNo"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refSubStance?.casNo,
                                        "onUpdate:value": value => (model.refSubStance.casNo = value),
                                        disabled: true
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                                )
                        })
                    ],
                    skeleton: isLoading.value
                })
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}labelPictogram`,
                colProp: () => ({ xs: 24, class: "mt-3" }),
                formItemRender: () => <div class="text-bold text-16">{i18n.global.t("model.hazDiscussPictogram.modelName")}</div>
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}pictograms`,
                            fetchCriteria: data.pictogram?.fetchCriteria,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "HazDiscussPictogramList" } })
                    );
                    return <HazDiscussPictogramList {...componentProp}></HazDiscussPictogramList>;
                }
            },
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}ld50ValueLabel`,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <div class="text-bold text-16">{i18n.global.t("views.request.ld50Value.label")}</div>
            },
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}ld50Value`,
                colProp: () => ({ xs: 24, md: 16 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 12 }, wrapperCol: { xs: 24, md: 12 } }),
                label: () => i18n.global.t("model.hazDiscussMainSubStance.ld50Value"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.ld50Value,
                            "onUpdate:value": value => (model.ld50Value = value),
                            disabled: viewOnly,
                            addonAfter: () => <div>mg/kg</div>
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refLd50`,
                colProp: () => ({ xs: 24, md: 4 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.hazDiscussMainSubStance.refLd50"),
                labelRender: () => "",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refLd50,
                            "onUpdate:value": value => (model.refLd50 = value),
                            disabled: true,
                            list: callLd50,
                            optLabel: "nameTh"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}ld50Operator`,
                colProp: () => ({ xs: 24, md: 4 }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                label: () => i18n.global.t("model.hazDiscussMainSubStance.ld50Operator"),
                labelRender: () => "",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.ld50Operator,
                            "onUpdate:value": value => (model.ld50Operator = value),
                            disabled: true,
                            list: callOperator,
                            optKey: "",
                            optLabel: "",
                            placeholder: ""
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}`) }
            }),
        ]
    });
};

export const initData = ({ props, data }) => {
    const { /* menuContext, viewOnly,  */ itemsKey /* , canCreate, canDelete */ } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    /*
    data.columns = createColumns({ props, data });
    data.page = createPage({ data: items });
    data.tableOptions = { search: { activeKey: null } };
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => (viewOnly ? false : canDelete),
            onClick: ({ rowSelection }) => {
                onClickDeleteAllClient({ rowSelection, useForm, items, key: itemsKey });
                data.page.page = 0;
            }
        }),
        createManageExportCsv({ menuContext })
    ];
    data.manageExtras = [
        {
            render: () => createButtonCreate({ onClick: () => items.push({ companies: [], products: [] }) }),
            visible: () => (viewOnly ? false : canCreate)
        }
    ];
    data.rowActions = [];
    */

    fetchData({ props, data }).then(page => items.push(...page.data));
};

export const fetchData = ({ props }) => {
    const { fetchCriteria } = props;
    if (!fetchCriteria || fetchCriteria.fetched) {
        return Promise.resolve({ data: [] });
    }
    fetchCriteria.fetched = true;
    setIsLoading(true);
    return callSearch({ criteria: fetchCriteria }).finally(() => setIsLoading(false));
};

/*export const calSubStanceRate = ({ type, qty }) => {
    if (!type || (qty?.toString?.()?.length || 0) === 0) return null;
    qty = parseFloat(qty);
    const factor = getFactorSubStanceRate({ type, qty });
    if (qty <= 2.5) {
        const rate = qty * factor;
        return { min: qty - rate, max: qty + rate };
    } else if (qty <= 50) {
        const rate = qty * factor;
        return { min: qty - rate, max: qty + rate };
    } else {
        return { min: qty - factor, max: qty + factor };
    }
};

export const getFactorSubStanceRate = ({ type, qty }) => {
    // 1 solid
    // 2 liquid
    if (qty <= 2.5) {
        return type === "1" ? 0.15 : 0.25;
    } else if (qty <= 10) {
        return type === "1" ? 0.1 : 0.1;
    } else if (qty <= 25) {
        return type === "1" ? 0.06 : 0.06;
    } else if (qty <= 60) {
        return type === "1" ? 0.05 : 0.05;
    } else {
        return type === "1" ? 2.5 : 2.5;
    }
};*/

export const createOnUpdateRefSubStance = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    return value => {
        const oldValue = model.refSubStance;
        model.refSubStance = value;
        if (oldValue != value) {
            model.otherSubStance = null;
            lodash.remove(model.pictograms, row => !row.id);
            model.pictograms.forEach(row => (row.isDelete = true));
        }
    };
};

export const filterSubStance = ({ subStances, hazDiscuss }) => {
    return callHazDiscussCase({
        criteria: {
            select: ["**"],
            page: 0,
            size: 1000000,
            where: [{ name: "isActive", type: "Boolean", operator: "=", value: true }],
            sort: [
                { name: "refSpecies.seq", type: "ASC" },
                { name: "refChoice.seq", type: "DESC" }
            ],
            children: [
                {
                    name: "discussCases",
                    select: ["*", "refLookupCase.*"],
                    page: 0,
                    size: 1000000
                },
                {
                    name: "subStances",
                    select: ["*", "refSubStance.*"],
                    page: 0,
                    size: 1000000
                }
            ]
        }
    }).then(hazDiscussCases => {
        //const hazDiscussCasesNotFisheries = hazDiscussCases.filter(d => !d.refSpecies?.key && !d.refPurpose?.key);
        //const hazDiscussCasesMedia = hazDiscussCases.filter(d => !d.refSpecies?.key && d.refPurpose?.key === "3");
        //const hazDiscussCases123All = hazDiscussCases.filter(
        //    d => ["1", "2", "3"].includes(d.refSpecies?.key) && ["1"].includes(d.refChoice?.key)
        //);
        const hazDiscussCases123Specify = hazDiscussCases.filter(
            d => ["1", "2", "3"].includes(d.refSpecies?.key) && ["2"].includes(d.refChoice?.key)
        );
        //const hazDiscussCases4 = hazDiscussCases.filter(d => ["4"].includes(d.refSpecies?.key));
        return subStances.filter(subStance => {
            /*
            if (hazDiscuss.refMainObjective?.key !== "3") {
                return false;
            }
            if (!subStance.refSpecies?.key) {
                return true;
            }
            const isSpecify = hazDiscussCases123Specify.some(hazDiscussCase => {
                if (!hazDiscussCase.subStances.map(d => d.refSubStance.id).includes(subStance.id)) {
                    return false;
                }
                return true;
            });
            const cases = isSpecify ? hazDiscussCases123Specify : hazDiscussCases123All;
            return cases.filter(hazDiscussCase => filterHazDiscussCase({ hazDiscuss, hazDiscussCase, subStance })).length > 0;
            */
            const isSpecify = hazDiscussCases123Specify.some(hazDiscussCase => {
                // check by name
                if (!hazDiscussCase.subStances.map(d => d.refSubStance.name).includes(subStance.name)) {
                    return false;
                }
                return true;
            });
            if (isSpecify) {
                const subStanceSpecifies = hazDiscussCases123Specify.filter(hazDiscussCase =>
                    filterHazDiscussCase({ hazDiscuss, hazDiscussCase, subStance })
                );
                if (subStanceSpecifies.length > 0) {
                    return subStanceSpecifies.some(hazDiscussCase => hazDiscussCase.refSpecies?.key === subStance.refSpecies?.key);
                } else {
                    return !["1", "2", "3"].includes(subStance.refSpecies?.key);
                }
            }
            return true;
        });
    });
};

export const filterHazDiscussCase = ({ hazDiscuss, hazDiscussCase /*, subStance */ }) => {
    /*
    if (hazDiscussCase.refSpecies?.key !== subStance.refSpecies?.key) {
        return false;
    }
    */
    if (hazDiscussCase.refPurpose?.key !== hazDiscuss.refMainObjective?.key) {
        return false;
    }
    if (!hazDiscussCase.discussCases.map(d => d.refLookupCase.key).includes(hazDiscuss.refSubObjective?.key)) {
        return false;
    }
    if (hazDiscussCase.refOperation?.key !== hazDiscuss.refMainOperation?.key) {
        return false;
    }
    if (hazDiscussCase.refSubOperation?.key !== hazDiscuss.refSubOperation?.key) {
        return false;
    }
    return true;
};
